// Update src/components/settings/shared/BookingSection.jsx
import React, { useEffect } from 'react';
import { Calendar, Clock, Users, DollarSign, Settings, AlertTriangle } from 'lucide-react';
import { FormField, TextInput, Toggle, SelectInput, TextArea } from './FormComponents';
import { validateBookingConfig } from '../../../services/bookingConfigurationService';

export const BookingSection = ({ formData, onInputChange }) => {
  // Validate booking configuration when data changes
  const [validationErrors, setValidationErrors] = React.useState({});
  
  useEffect(() => {
    if (formData.enableBooking) {
      const errors = validateBookingConfig(formData);
      setValidationErrors(errors);
    } else {
      setValidationErrors({});
    }
  }, [formData]);

  const serviceTypes = [
    { value: 'appointment', label: 'Appointments & Consultations', description: 'One-on-one meetings with staff' },
    { value: 'consultation', label: 'Strategic Consultations', description: 'Planning and advisory sessions' },
    { value: 'class', label: 'Classes & Workshops', description: 'Group learning and training sessions' },
    { value: 'facility', label: 'Facility Bookings', description: 'Meeting rooms and event spaces' },
    { value: 'event', label: 'Event Registration', description: 'Workshops, seminars, and events' }
  ];

  const paymentMethods = [
    { value: 'free', label: 'Free Services Only', description: 'No payment required' },
    { value: 'manual', label: 'Manual Payment', description: 'Invoice or cash payment' },
    { value: 'stripe', label: 'Online Payments (Stripe)', description: 'Credit/debit cards online' },
    { value: 'payfast', label: 'South African Payments (PayFast)', description: 'Local payment gateway' }
  ];

  const timezones = [
    { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'Europe/London', label: 'GMT/BST' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking System</h3>
        <p className="text-gray-600">
          Enable online booking and appointment scheduling for your organization
        </p>
      </div>

      {/* Validation Errors Summary */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Configuration Issues</h4>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {Object.entries(validationErrors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Enable Booking System */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Booking System</h4>
        </div>
        
        <Toggle
          checked={formData.enableBooking || false}
          onChange={(value) => onInputChange('enableBooking', value)}
          label="Enable Booking System"
          description="Allow visitors to book appointments, classes, and facilities online"
        />

        {formData.enableBooking && (
          <div className="space-y-6 pl-6 border-l-2 border-blue-100">
            {/* Basic Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField 
                label="Organization Display Name" 
                error={validationErrors.bookingOrgName}
                description="How your organization appears to customers"
              >
                <TextInput
                  value={formData.bookingOrgName || ''}
                  onChange={(value) => onInputChange('bookingOrgName', value)}
                  placeholder="Your Organization Name"
                  error={validationErrors.bookingOrgName}
                />
              </FormField>

              <FormField 
                label="Default Timezone"
                description="All booking times will be shown in this timezone"
              >
                <SelectInput
                  value={formData.bookingTimezone || 'Africa/Johannesburg'}
                  onChange={(value) => onInputChange('bookingTimezone', value)}
                  options={timezones}
                />
              </FormField>
            </div>

            {/* Service Types */}
            <div>
              <FormField 
                label="Enabled Service Types" 
                description="What types of bookings do you want to offer?"
                error={validationErrors.bookingServices}
              >
                <div className="space-y-4">
                  {serviceTypes.map(service => (
                    <div key={service.value} className="border border-gray-200 rounded-lg p-4">
                      <Toggle
                        checked={formData.bookingServices?.[service.value] || false}
                        onChange={(value) => onInputChange(`bookingServices.${service.value}`, value)}
                        label={service.label}
                        description={service.description}
                      />
                    </div>
                  ))}
                </div>
              </FormField>
            </div>

            {/* Business Hours */}
            <div>
              <FormField 
                label="Default Business Hours" 
                description="Set your standard operating hours for bookings"
              >
                <div className="space-y-3">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div key={day} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg">
                      <div className="w-24 text-sm font-medium capitalize">{day}</div>
                      <Toggle
                        checked={formData.businessHours?.[day]?.enabled || false}
                        onChange={(value) => onInputChange(`businessHours.${day}.enabled`, value)}
                        label=""
                      />
                      {formData.businessHours?.[day]?.enabled && (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={formData.businessHours?.[day]?.start || '09:00'}
                            onChange={(e) => onInputChange(`businessHours.${day}.start`, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="time"
                            value={formData.businessHours?.[day]?.end || '17:00'}
                            onChange={(e) => onInputChange(`businessHours.${day}.end`, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </FormField>
            </div>

            {/* Payment Configuration */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <h5 className="font-medium text-gray-900">Payment Settings</h5>
              </div>

              <FormField label="Payment Methods">
                <div className="space-y-3">
                  {paymentMethods.map(method => (
                    <label key={method.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="bookingPaymentMethod"
                        value={method.value}
                        checked={formData.bookingPaymentMethod === method.value}
                        onChange={(e) => onInputChange('bookingPaymentMethod', e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{method.label}</div>
                        <div className="text-sm text-gray-600">{method.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </FormField>

              {/* Stripe Configuration */}
              {formData.bookingPaymentMethod === 'stripe' && (
                <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                  <h6 className="font-medium text-blue-900">Stripe Configuration</h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField 
                      label="Stripe Publishable Key"
                      error={validationErrors.stripePublishableKey}
                    >
                      <TextInput
                        value={formData.stripePublishableKey || ''}
                        onChange={(value) => onInputChange('stripePublishableKey', value)}
                        placeholder="pk_test_..."
                        error={validationErrors.stripePublishableKey}
                      />
                    </FormField>
                    <FormField 
                      label="Stripe Secret Key"
                      error={validationErrors.stripeSecretKey}
                    >
                      <TextInput
                        value={formData.stripeSecretKey || ''}
                        onChange={(value) => onInputChange('stripeSecretKey', value)}
                        placeholder="sk_test_..."
                        type="password"
                        error={validationErrors.stripeSecretKey}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {/* PayFast Configuration */}
              {formData.bookingPaymentMethod === 'payfast' && (
                <div className="bg-green-50 p-4 rounded-lg space-y-4">
                  <h6 className="font-medium text-green-900">PayFast Configuration</h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField 
                      label="PayFast Merchant ID"
                      error={validationErrors.payfastMerchantId}
                    >
                      <TextInput
                        value={formData.payfastMerchantId || ''}
                        onChange={(value) => onInputChange('payfastMerchantId', value)}
                        placeholder="10000100"
                        error={validationErrors.payfastMerchantId}
                      />
                    </FormField>
                    <FormField 
                      label="PayFast Merchant Key"
                      error={validationErrors.payfastMerchantKey}
                    >
                      <TextInput
                        value={formData.payfastMerchantKey || ''}
                        onChange={(value) => onInputChange('payfastMerchantKey', value)}
                        placeholder="46f0cd694581a"
                        type="password"
                        error={validationErrors.payfastMerchantKey}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </div>

            {/* Advanced Settings */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                <h5 className="font-medium text-gray-900">Advanced Options</h5>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Default Booking Duration">
                  <SelectInput
                    value={formData.defaultBookingDuration || '60'}
                    onChange={(value) => onInputChange('defaultBookingDuration', value)}
                    options={[
                      { value: '15', label: '15 minutes' },
                      { value: '30', label: '30 minutes' },
                      { value: '60', label: '1 hour' },
                      { value: '90', label: '1.5 hours' },
                      { value: '120', label: '2 hours' }
                    ]}
                  />
                </FormField>

                <FormField label="Buffer Time Between Bookings">
                  <SelectInput
                    value={formData.bookingBuffer || '15'}
                    onChange={(value) => onInputChange('bookingBuffer', value)}
                    options={[
                      { value: '0', label: 'No buffer' },
                      { value: '5', label: '5 minutes' },
                      { value: '15', label: '15 minutes' },
                      { value: '30', label: '30 minutes' }
                    ]}
                  />
                </FormField>
              </div>

              <div className="space-y-4">
                <Toggle
                  checked={formData.requireBookingApproval || false}
                  onChange={(value) => onInputChange('requireBookingApproval', value)}
                  label="Require Manual Approval"
                  description="All bookings need staff approval before confirmation"
                />
                
                <Toggle
                  checked={formData.sendBookingNotifications !== false}
                  onChange={(value) => onInputChange('sendBookingNotifications', value)}
                  label="Send Email Notifications"
                  description="Automatically send confirmation and reminder emails"
                />
                
                <Toggle
                  checked={formData.allowRescheduling !== false}
                  onChange={(value) => onInputChange('allowRescheduling', value)}
                  label="Allow Customer Rescheduling"
                  description="Customers can reschedule their own bookings"
                />
                
                <Toggle
                  checked={formData.enableBookingPortal || false}
                  onChange={(value) => onInputChange('enableBookingPortal', value)}
                  label="Enable Internal Booking Portal"
                  description="Show booking management in the organization portal"
                />
              </div>

              {/* Policies */}
              <div className="space-y-4">
                <FormField label="Cancellation Policy">
                  <TextArea
                    value={formData.cancellationPolicy || '24-hour notice required for cancellations. Late cancellations may incur charges.'}
                    onChange={(value) => onInputChange('cancellationPolicy', value)}
                    placeholder="Describe your cancellation policy..."
                    rows={3}
                  />
                </FormField>

                <FormField label="Terms and Conditions (Optional)">
                  <TextArea
                    value={formData.bookingTerms || ''}
                    onChange={(value) => onInputChange('bookingTerms', value)}
                    placeholder="Additional terms for bookings..."
                    rows={4}
                  />
                </FormField>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};